// Wave 20.26 — Static route map for outdoor session detail pages.
//
// Draws a polyline from a lat/lng list on a dark Mapbox basemap, fits
// the map to the route bounds, and disables all interaction gestures
// (this is a "look at my ride" view, not an interactive map). If
// mapbox-gl fails to init (missing token, offline) the component
// silently falls back to a plain empty box — the page still renders.

import React, { useEffect, useRef, useState } from 'react';
import { Box, Slider, Stack, Typography } from '@mui/material';
import NavigationIcon from '@mui/icons-material/Navigation';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import OpacityIcon from '@mui/icons-material/Opacity';
import mapboxgl from 'mapbox-gl';
import { attachMinimalAttribution } from '../services/mapAttribution';
import 'mapbox-gl/dist/mapbox-gl.css';
import { colors } from '../theme';
import { fmtSpeed, fmtTemp, useUnitSystem } from '../services/useUnitSystem';
import { useMapStyle, mapStyleUrl } from '../services/useMapStyle';
import { cameraPinSvgString } from './mapPinGlyphs';
import WindParticles from './WindParticles';

const COMPASS = ['N','NNE','NE','ENE','E','ESE','SE','SSE','S','SSW','SW','WSW','W','WNW','NW','NNW'];
function bearingLabel(deg: number | null | undefined): string {
  if (deg == null) return '';
  const idx = Math.round(((deg % 360) + 360) % 360 / 22.5) % 16;
  return COMPASS[idx];
}

// Wave 20.49dh — Weather goes through the api proxy.
const API_BASE = process.env.REACT_APP_API || 'https://api.aero.chat';
const WEATHER_API = `${API_BASE}/weather`;
const TERRAIN_API =
  process.env.REACT_APP_TERRAIN_API || 'https://terrain.aero.chat';
const TOKEN_KEY = process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token';

// Public Mapbox access token — same aero.chat pk token as the
// scroll hero. Safe to embed (pk tokens are read-only against the
// account's public styles). Rotate at account.mapbox.com if needed.
const MAPBOX_TOKEN =
  process.env.REACT_APP_MAPBOX_TOKEN ||
  process.env.REACT_APP_MAPBOX_ACCESS_TOKEN ||
  'pk.eyJ1IjoiYWVyby1jaGF0IiwiYSI6ImNtY3J4bnp0MTBjeTYybm42bzVjbWtsdmkifQ.rbgswhST93lMRY-CzQZ05Q';

mapboxgl.accessToken = MAPBOX_TOKEN;

// Popup HTML is user-derived (labels come from a data source we
// trust, but they include free-form text from the peak formatter).
// Escape defensively — Mapbox writes .setHTML() straight to innerHTML.
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export interface RouteMapPoint {
  lat: number;
  lng: number;
  /** Seconds since activity start. Only required when passing
   *  `highlights` — used to slice the polyline into peak segments. */
  t?: number;
}

export interface RouteHighlight {
  startSec: number;
  endSec: number;
  /** Optional label — shown on hover / tap. */
  label?: string;
}

/** Wave 20.32 — the currently-selected peak. When set, its polyline
 *  slice paints on top of everything else at full brightness and a
 *  Mapbox popup pins the metric / duration / distance to the
 *  segment's midpoint. */
export interface SelectedPeak {
  startSec: number;
  endSec: number;
  valueLabel: string;   // pre-formatted (unit-aware) by the caller
  durationLabel: string;
  distanceLabel?: string | null;
}

interface Props {
  points: RouteMapPoint[];
  height?: number | string;
  /** Wave 20.26 — highlight sub-segments of the polyline where the
   *  state-machine detector found sustained peaks. Requires each
   *  point to carry a `t` so we can pick the right slice. */
  highlights?: RouteHighlight[];
  /** Wave 20.32 — one specific peak highlighted brighter than the
   *  rest + tooltip pinned to its midpoint. */
  selectedPeak?: SelectedPeak | null;
  /** Wave 20.30 — when both are given, mount a windy.com-style
   *  particle overlay that loops through the model timesteps the
   *  session lived across. Timestamps are ISO-8601. */
  windReplay?: {
    startedAt: string;
    endedAt: string;
  };
  /** Wave 20.38a — 3D terrain rendering via self-hosted terrain-RGB
   *  tiles from terrain.aero.chat. Off by default; opt in per
   *  session-detail page or wherever this map is used. */
  terrain3D?: boolean;
  /** Wave 20.38b — vertical exaggeration for terrain3D, 1..10. 1x is
   *  realistic; 3x makes hills legible on typical routes; 10x is
   *  useful for browsing flat routes where even 30 m rollers only
   *  read at high exaggeration. Default 3. Ignored when terrain3D
   *  is false. */
  terrainExaggeration?: number;
  /** Wave 20.38a — moving playback marker (session replay). Renders
   *  a small brand-colored circle at the given lat/lng, moves
   *  imperatively on prop change so 60fps updates don't churn React. */
  playbackMarker?: { lat: number; lng: number } | null;
  /** Wave 20.38a — when true, camera easeTo's toward the playback
   *  marker each time it moves, so the map follows the rider. */
  followPlayback?: boolean;
  /** Wave 20.38a — seconds since session start of the current
   *  replay position. Used to pin the wind particle field to the
   *  historical model timestep at that moment, so scrubbing /
   *  playing the replay makes the wind change with time instead of
   *  auto-looping independently. */
  replayPosSec?: number | null;
  /** Wave 20.38c — When true, the built-in top-left weather chip
   *  is not rendered. Use when the parent is showing its own
   *  weather readout (e.g. the fullscreen replay HUD). */
  hideWeatherChip?: boolean;
  /** Wave 20.38c — fires whenever the currently-displayed wind
   *  frame changes (scrub pin, replay pin, or auto-loop advance).
   *  Parent can consume for a unified HUD. */
  onWeatherChange?: (frame: WindFrameSpec | null) => void;
  /** Wave 20.49cb — Media pins to render on the map (photos +
   *  videos the rider attached to the session). Each renders as
   *  a 📷 emoji marker at the geotag. Clicking opens the media in
   *  the parent's viewer via onMediaPinClick. */
  mediaPins?: Array<{ id: string; lat: number; lng: number }>;
  onMediaPinClick?: (mediaId: string) => void;
}

export interface WindFrameSpec {
  url: string;
  rideSec?: number;
  bbox: [number, number, number, number];
  // Wave 20.38a — per-frame weather at the session centroid,
  // pulled from the /wind/manifest response.
  t?: string;
  temperature_c?: number | null;
  humidity_pct?: number | null;
  wind_speed_ms?: number | null;
  wind_from_deg?: number | null;
  condition?: string | null;
}

export default function RouteMap({ points, height = 320, highlights, selectedPeak, windReplay, terrain3D, terrainExaggeration = 3, playbackMarker, followPlayback, replayPosSec, hideWeatherChip, onWeatherChange, mediaPins, onMediaPinClick }: Props) {
  const container = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const [mapReady, setMapReady] = useState<mapboxgl.Map | null>(null);
  const [windFrames, setWindFrames] = useState<WindFrameSpec[]>([]);
  const [rideDurationSec, setRideDurationSec] = useState<number>(0);
  // Wave 20.38a — currently-displayed frame index. Advances via the
  // WindParticles onFrameChange callback (auto-loop) or via user
  // scrub on the slider (which pauses the auto-loop).
  const [currentIdx, setCurrentIdx] = useState<number>(0);
  const [scrubOverrideIdx, setScrubOverrideIdx] = useState<number | null>(null);
  const units = useUnitSystem();
  const mapStyle = useMapStyle();
  useEffect(() => {
    if (!container.current) return;
    if (points.length < 2) return;

    const map = new mapboxgl.Map({
      container: container.current,
      // Wave 20.49 — user-chosen basemap. Default `outdoors-v12`
      // has bright hillshade + trails and pairs well with the
      // brand-green polyline; `dark-v11` remains available for
      // riders who prefer the original theme.
      style: mapStyleUrl(mapStyle),
      // Wave 20.38a — allow the rider to tilt / rotate when terrain3D
      // is on so they can actually SEE the terrain. Pan / zoom still
      // free either way; fitBounds on load frames the route so nobody
      // wanders off it by accident.
      interactive: terrain3D ? true : false,
      pitchWithRotate: true,
      dragRotate: !!terrain3D,
      touchPitch: !!terrain3D,
      // Initial pitch when terrain is on — otherwise the camera is
      // straight-down and hills look flat regardless of setTerrain().
      pitch: terrain3D ? 55 : 0,
      bearing: 0,
      attributionControl: false,
      logoPosition: 'bottom-right',
      center: [points[0].lng, points[0].lat],
      zoom: 12,
      // Wave 20.49bl — no tile crossfade on mount.
      fadeDuration: 0,
    });
    mapRef.current = map;
    attachMinimalAttribution(map);

    map.on('load', () => {
      map.addSource('route', {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: points.map((p) => [p.lng, p.lat]),
          },
        },
      });
      // Base track — dim when there are highlights so the peaks
      // pop, brand-green otherwise so a plain route still looks nice.
      // Wave 20.45 — rendered as a stacked pipe: dark casing beneath
      // the brand-color fill. On 3D terrain this reads as a raised
      // tube rather than a flat scribble that gets lost in shading.
      const hasHighlights = !!highlights && highlights.length > 0 && points.some((p) => p.t != null);
      map.addLayer({
        id: 'route-casing',
        type: 'line',
        source: 'route',
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': '#0a0d12',
          'line-width': hasHighlights ? 6 : 9,
          'line-opacity': 0.85,
        },
      });
      map.addLayer({
        id: 'route',
        type: 'line',
        source: 'route',
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': hasHighlights ? '#8a94a8' : colors.brand,
          'line-width': hasHighlights ? 3.5 : 6,
          'line-emissive-strength': 1.6,
          'line-blur': 0.4,
        },
      });

      // Wave 20.26 — peak-segment overlays. One line feature per
      // detected peak, sliced from the base track by t ∈ [start, end].
      if (hasHighlights && highlights) {
        const features = highlights
          .map((h, i) => {
            const seg = points.filter(
              (p) => p.t != null && p.t >= h.startSec && p.t <= h.endSec,
            );
            if (seg.length < 2) return null;
            return {
              type: 'Feature' as const,
              properties: { idx: i, label: h.label ?? '' },
              geometry: {
                type: 'LineString' as const,
                coordinates: seg.map((p) => [p.lng, p.lat]),
              },
            };
          })
          .filter(Boolean) as Array<GeoJSON.Feature<GeoJSON.LineString>>;
        if (features.length > 0) {
          map.addSource('peaks', {
            type: 'geojson',
            data: { type: 'FeatureCollection', features },
          });
          map.addLayer({
            id: 'peaks-casing',
            type: 'line',
            source: 'peaks',
            layout: { 'line-cap': 'round', 'line-join': 'round' },
            paint: {
              'line-color': '#0a0d12',
              'line-width': 10,
              'line-opacity': 0.9,
            },
          });
          map.addLayer({
            id: 'peaks',
            type: 'line',
            source: 'peaks',
            layout: { 'line-cap': 'round', 'line-join': 'round' },
            paint: {
              'line-color': colors.brand,
              'line-width': 7,
              'line-opacity': 0.98,
              'line-emissive-strength': 1.8,
              'line-blur': 0.3,
            },
          });
        }
      }

      // Start + end markers so the direction is obvious even without arrows.
      const start = points[0];
      const end = points[points.length - 1];
      new mapboxgl.Marker({ color: colors.brand, scale: 0.6 })
        .setLngLat([start.lng, start.lat])
        .addTo(map);
      new mapboxgl.Marker({ color: colors.data ?? '#00d9ff', scale: 0.6 })
        .setLngLat([end.lng, end.lat])
        .addTo(map);
      const bounds = points.reduce(
        (b, p) => b.extend([p.lng, p.lat]),
        new mapboxgl.LngLatBounds(),
      );
      // Wave 20.38b — pass pitch explicitly. Without it, some code
      // paths (route re-init on intervals arriving) reset the camera
      // to pitch=0 and the terrain view flattens out mid-load.
      map.fitBounds(bounds, {
        padding: 24,
        duration: 0,
        ...(terrain3D ? { pitch: 55 } : {}),
      });

      setMapReady(map);
    });

    // Wave 20.38a — Fullscreen containers (MUI Dialog etc.) transition
    // in after Mapbox has already measured the container as 0×0, so
    // without a resize on grow the canvas stays a black rectangle
    // regardless of layout size. Same fix pattern as the weather-panel
    // fullscreen — one ResizeObserver + a delayed backstop resize.
    let ro: ResizeObserver | null = null;
    if (container.current) {
      ro = new ResizeObserver(() => {
        if (mapRef.current) mapRef.current.resize();
      });
      ro.observe(container.current);
    }
    const resizeBackstop = window.setTimeout(() => {
      if (mapRef.current) mapRef.current.resize();
    }, 350);

    return () => {
      try {
        ro?.disconnect();
        window.clearTimeout(resizeBackstop);
        map.remove();
      } catch {
        /* ignore teardown errors */
      }
      mapRef.current = null;
      setMapReady(null);
    };
  }, [points, highlights, mapStyle]);

  // Wave 20.32 — draw / update the "selected peak" overlay in-place
  // without tearing the map down. Adds an extra line layer on top of
  // the `peaks` layer at ~1.7× width so a chosen peak visibly leads
  // the rest, plus a mapbox Popup pinned to the segment midpoint.
  const selPopupRef = useRef<mapboxgl.Popup | null>(null);
  const playbackMarkerRef = useRef<mapboxgl.Marker | null>(null);

  // Wave 20.38b — terrain lifecycle. Kept in its own effect (rather
  // than a one-shot inside the map's 'load' handler) so it's
  // idempotent: any time the map instance, terrain3D flag, or
  // exaggeration changes, we re-add the source / hillshade / sky
  // and re-call setTerrain with the new exaggeration. This also
  // recovers if the map style ever gets reset (fresh map after
  // highlights change, style reload, etc.) — the effect re-runs
  // on mapReady and reapplies terrain.
  useEffect(() => {
    const map = mapReady;
    if (!map) return;
    if (!terrain3D) {
      try {
        map.setTerrain(null);
      } catch {
        /* ignore */
      }
      return;
    }
    const apply = () => {
      try {
        if (!map.getSource('aerochat-terrain')) {
          map.addSource('aerochat-terrain', {
            type: 'raster-dem',
            tiles: [`${TERRAIN_API}/tiles/{z}/{x}/{y}.png`],
            tileSize: 256,
            maxzoom: 14,
            encoding: 'terrarium',
          });
        }
        // Wave 20.38c — colorized-altitude raster overlay. We were
        // trying to use hillshade to show relief on dark-v11 and
        // it just wasn't visible enough. Same approach as mobile:
        // load the terrarium tiles as a plain raster source, then
        // remap RGB → elevation via rasterColorMix, then color the
        // altitude with rasterColor. On dark-v11, this literally
        // paints landmass by altitude (transparent at sea level,
        // brand green at low, warm tans mid, near-white at peaks).
        if (!map.getSource('aerochat-terrain-rgb')) {
          map.addSource('aerochat-terrain-rgb', {
            type: 'raster',
            tiles: [`${TERRAIN_API}/tiles/{z}/{x}/{y}.png`],
            tileSize: 256,
            maxzoom: 14,
          });
        }
        if (!map.getLayer('aerochat-terrain-color')) {
          map.addLayer(
            {
              id: 'aerochat-terrain-color',
              type: 'raster',
              source: 'aerochat-terrain-rgb',
              paint: {
                'raster-opacity': 0.7,
                // Wave 20.38c — reconstruct terrarium elevation from
                // RGB. Mapbox normalizes src.r/g/b to [0..1] before
                // applying the mix, so raw-byte terrarium
                // coefficients (256, 1, 1/256, -32768) must be
                // multiplied by 255 to compensate:
                //   E = src.r*65280 + src.g*255 + src.b*0.996 - 32768
                // Was previously (256, 1, 1/256, -32768) which read
                // every pixel as ~-32768 → clamped to the transparent
                // end of the color ramp → invisible terrain.
                'raster-color-mix': [65280, 255, 255 / 256, -32768],
                'raster-color-range': [0, 3000],
                'raster-color': [
                  'interpolate',
                  ['linear'],
                  ['raster-value'],
                  0, 'rgba(74,222,128,0.0)',
                  200, 'rgba(74,222,128,0.35)',
                  800, 'rgba(180,190,110,0.55)',
                  1600, 'rgba(210,150,90,0.75)',
                  2400, 'rgba(230,210,190,0.9)',
                  3000, 'rgba(255,255,255,1.0)',
                ],
              } as any,
            },
            map.getLayer('route') ? 'route' : undefined,
          );
        }
        if (!map.getLayer('aerochat-sky')) {
          map.addLayer({
            id: 'aerochat-sky',
            type: 'sky',
            paint: {
              'sky-type': 'atmosphere',
              'sky-atmosphere-sun': [0.0, 90.0],
              'sky-atmosphere-sun-intensity': 5,
            } as any,
          });
        }
        map.setTerrain({
          source: 'aerochat-terrain',
          exaggeration: Math.max(1, Math.min(10, terrainExaggeration)),
        });
      } catch {
        /* style not yet ready, effect will re-run on mapReady */
      }
    };
    apply();
    // Some Mapbox operations (setStyle, tile-source hot-swap) can
    // wipe terrain without notifying us. Re-apply on styledata so
    // terrain never silently disappears mid-session.
    const onStyle = () => apply();
    map.on('styledata', onStyle);
    return () => {
      map.off('styledata', onStyle);
    };
  }, [mapReady, terrain3D, terrainExaggeration]);

  // Wave 20.38a — session replay marker. Imperative update on prop
  // change so a 60 fps playback loop doesn't force React to reconcile
  // the whole RouteMap subtree each animation frame.
  //
  // CRITICAL: depend on `mapReady` (the state, not `mapRef.current`).
  // The map instance is created inside another effect + only becomes
  // usable when its 'load' handler fires and setMapReady is called.
  // If we key this effect off refs alone the marker attach can miss
  // that window and never mount. Same class of bug hit the weather
  // panel fullscreen + wind replay before.
  useEffect(() => {
    const map = mapReady;
    if (!map) return;
    if (!playbackMarker) {
      if (playbackMarkerRef.current) {
        playbackMarkerRef.current.remove();
        playbackMarkerRef.current = null;
      }
      return;
    }
    if (!playbackMarkerRef.current) {
      const el = document.createElement('div');
      el.style.width = '18px';
      el.style.height = '18px';
      el.style.borderRadius = '50%';
      el.style.backgroundColor = colors.brand;
      el.style.border = '3px solid #ffffff';
      el.style.boxShadow = '0 0 0 2px rgba(0,0,0,0.55)';
      el.style.cursor = 'default';
      playbackMarkerRef.current = new mapboxgl.Marker(el)
        .setLngLat([playbackMarker.lng, playbackMarker.lat])
        .addTo(map);
    } else {
      playbackMarkerRef.current.setLngLat([playbackMarker.lng, playbackMarker.lat]);
    }
    if (followPlayback) {
      // Wave 20.49bj — snap, don't fly. Rider asked us to
      // remove the fly-in animation from every map.
      map.jumpTo({
        center: [playbackMarker.lng, playbackMarker.lat],
      });
    }
  }, [mapReady, playbackMarker, followPlayback]);

  useEffect(() => {
    return () => {
      if (playbackMarkerRef.current) {
        playbackMarkerRef.current.remove();
        playbackMarkerRef.current = null;
      }
    };
  }, []);

  // Wave 20.49cb — Media pin markers. One 📷 emoji per media
  // item's geotag, clickable to open the parent's viewer.
  const mediaMarkersRef = useRef<mapboxgl.Marker[]>([]);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    // Wipe existing markers.
    for (const m of mediaMarkersRef.current) m.remove();
    mediaMarkersRef.current = [];
    if (!mediaPins || mediaPins.length === 0) return;
    for (const pin of mediaPins) {
      if (!Number.isFinite(pin.lat) || !Number.isFinite(pin.lng)) continue;
      const el = document.createElement('div');
      // Wave 20.49ce — Aero.chat branded camera pin (SVG) in
      // place of the raw 📷 emoji.
      el.innerHTML = cameraPinSvgString(34);
      el.setAttribute('role', 'button');
      el.setAttribute('aria-label', 'Open photo');
      el.style.cssText = [
        'cursor:pointer',
        'user-select:none',
        'display:flex',
        'align-items:flex-end',
        'justify-content:center',
      ].join(';');
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        if (onMediaPinClick) onMediaPinClick(pin.id);
      });
      const marker = new mapboxgl.Marker({ element: el, anchor: 'bottom' })
        .setLngLat([pin.lng, pin.lat])
        .addTo(map);
      mediaMarkersRef.current.push(marker);
    }
    return () => {
      for (const m of mediaMarkersRef.current) m.remove();
      mediaMarkersRef.current = [];
    };
  }, [mediaPins, mapReady, onMediaPinClick]);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const clear = () => {
      try {
        if (map.getLayer('selected-peak')) map.removeLayer('selected-peak');
        if (map.getSource('selected-peak')) map.removeSource('selected-peak');
      } catch { /* ignore */ }
      if (selPopupRef.current) {
        selPopupRef.current.remove();
        selPopupRef.current = null;
      }
    };
    clear();
    if (!selectedPeak) return;
    const seg = points.filter(
      (p) => p.t != null && p.t >= selectedPeak.startSec && p.t <= selectedPeak.endSec,
    );
    if (seg.length < 2) return;
    // `load` can fire before Mapbox's style is fully addSource-able.
    // Guard on isStyleLoaded and, when false, retry once idle.
    if (!map.isStyleLoaded()) {
      const retry = () => {
        map.off('idle', retry);
        // Re-run this whole effect on the next tick — deps unchanged
        // so React skips it. Simplest: call the same DOM operations
        // inline here after the check passes.
        try { addSelection(); } catch { /* ignore */ }
      };
      map.on('idle', retry);
      return () => {
        map.off('idle', retry);
        clear();
      };
    }
    addSelection();
    return clear;

    function addSelection() {
      if (!map) return;
      // Style might have finished between the guard and now if we
      // routed through the idle handler; addSource still requires it.
      if (!map.isStyleLoaded()) return;
      // Clear again in case the effect fired twice (idle + immediate).
      try {
        if (map.getLayer('selected-peak')) map.removeLayer('selected-peak');
        if (map.getSource('selected-peak')) map.removeSource('selected-peak');
      } catch { /* ignore */ }
      map.addSource('selected-peak', {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: seg.map((p) => [p.lng, p.lat]),
          },
        },
      });
      map.addLayer({
        id: 'selected-peak',
        type: 'line',
        source: 'selected-peak',
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': '#ffffff',
          'line-width': 10,
          'line-opacity': 0.98,
          'line-emissive-strength': 2,
          'line-blur': 0.3,
        },
      });
      // Zoom-to-fit the peak segment so the user sees where it was.
      const segBounds = seg.reduce(
        (b, p) => b.extend([p.lng, p.lat]),
        new mapboxgl.LngLatBounds(),
      );
      // Wave 20.49bj — snap, no fly-in (rider-wide ask).
      map.fitBounds(segBounds, { padding: 60, duration: 0, maxZoom: 16 });
      // Popup pinned to the segment midpoint.
      const mid = seg[Math.floor(seg.length / 2)];
      const parts: string[] = [selectedPeak!.valueLabel];
      if (selectedPeak!.durationLabel) parts.push(selectedPeak!.durationLabel);
      if (selectedPeak!.distanceLabel) parts.push(selectedPeak!.distanceLabel);
      const html = `
        <div style="
          font-family: -apple-system, BlinkMacSystemFont, sans-serif;
          color: ${colors.text};
          font-weight: 800;
          font-size: 13px;
          line-height: 1.3;
        ">
          ${parts.map((p, i) => `<div style="${
            i === 0 ? `color:${colors.brand};font-size:15px;` : `color:${colors.textMuted};font-size:11px;font-weight:600;`
          }">${escapeHtml(p)}</div>`).join('')}
        </div>
      `;
      selPopupRef.current = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
        offset: 12,
        maxWidth: '220px',
      })
        .setLngLat([mid.lng, mid.lat])
        .setHTML(html)
        .addTo(map);
    }
  }, [mapReady, selectedPeak, points]);

  // Fetch a wind-manifest once we know the route's bbox and the
  // session's time window. The manifest lists every model timestep
  // that overlaps the ride ± 1h with pre-baked PNG URLs.
  useEffect(() => {
    if (!windReplay || !mapReady || points.length < 2) return;
    let cancelled = false;
    const load = async () => {
      const b = mapReady.getBounds();
      // Padding so particles at the map edge still find valid wind
      // cells; clamp W/E/S/N so out-of-range Mapbox bounds don't 400.
      const pad = 0.5;
      let w = b.getWest() - pad;
      let e = b.getEast() + pad;
      let s = b.getSouth() - pad;
      let n = b.getNorth() + pad;
      if (e - w >= 360) {
        w = -180;
        e = 180;
      } else {
        w = Math.max(-180, Math.min(180, w));
        e = Math.max(-180, Math.min(180, e));
      }
      s = Math.max(-89.9, Math.min(89.9, s));
      n = Math.max(-89.9, Math.min(89.9, n));
      const bbox = `${w.toFixed(3)},${s.toFixed(3)},${e.toFixed(3)},${n.toFixed(3)}`;
      const jwt = localStorage.getItem(TOKEN_KEY);
      if (!jwt) return;
      const url = `${WEATHER_API}/wind/manifest?bbox=${encodeURIComponent(bbox)}&start=${encodeURIComponent(windReplay.startedAt)}&end=${encodeURIComponent(windReplay.endedAt)}&size=384x192`;
      try {
        const res = await fetch(url, { headers: { Authorization: `Bearer ${jwt}` } });
        if (!res.ok) return;
        const body = await res.json();
        if (cancelled) return;
        const started = new Date(windReplay.startedAt).getTime();
        const ended = new Date(windReplay.endedAt).getTime();
        setRideDurationSec(Math.max(60, (ended - started) / 1000));
        setWindFrames(
          (body.frames as Array<{
            t: string;
            ride_sec: number;
            png_url: string;
            temperature_c?: number | null;
            humidity_pct?: number | null;
            wind_speed_ms?: number | null;
            wind_from_deg?: number | null;
            condition?: string | null;
          }>).map((f) => ({
            url: `${WEATHER_API}${f.png_url}`,
            rideSec: f.ride_sec,
            bbox: [w, s, e, n],
            t: f.t,
            temperature_c: f.temperature_c,
            humidity_pct: f.humidity_pct,
            wind_speed_ms: f.wind_speed_ms,
            wind_from_deg: f.wind_from_deg,
            condition: f.condition,
          })),
        );
      } catch {
        // Silent — the route stays visible without particles.
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [windReplay, mapReady, points]);

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height,
        borderRadius: 2,
        overflow: 'hidden',
        border: `1px solid ${colors.border}`,
        backgroundColor: colors.surface,
      }}
    >
      <Box ref={container} sx={{ position: 'absolute', inset: 0 }} />
      {/* Wave 20.49bk — Removed the user-triggered 360° rotate
          button. Rider asked us to remove every fly-in animation
          from every map component; a 12 s rotation qualifies.
          If the rider wants to look around the terrain, the map
          is already interactive (pinch, right-click drag). */}
      {mapReady && windFrames.length > 0 && (() => {
        // Wave 20.38a — replay-driven pinned frame. Picks the frame
        // whose rideSec is <= replayPosSec (i.e. the last-published
        // model timestep that happened before this moment of the
        // ride). Scrubbing / playing the SessionReplayControls will
        // make wind + weather change with time.
        const replayPinnedIdx = (() => {
          if (replayPosSec == null) return null;
          let idx = 0;
          for (let i = 0; i < windFrames.length; i++) {
            const fs = windFrames[i].rideSec ?? 0;
            if (fs <= replayPosSec) idx = i;
            else break;
          }
          return idx;
        })();
        const effectivePin = replayPinnedIdx ?? scrubOverrideIdx;
        return (
          <>
            {/* Wave 20.38c — CRITICAL: no `key` prop here. Previously
                keyed the component off the effective pin index, which
                remounted WindParticles on every frame change during
                playback — the canvas + particle system reset before
                any single frame's texture could actually decode +
                render. Result: wind vis was invisible while playing.
                Now WindParticles updates its texture in place (via
                the pinnedFrameIdx effect) with no remount. */}
            <WindParticles
              map={mapReady as any}
              frames={windFrames}
              rideDurationSec={effectivePin != null ? 0 : rideDurationSec}
              loopWallSec={Math.min(60, Math.max(20, rideDurationSec / 30))}
              pinnedFrameIdx={effectivePin}
              onFrameChange={(idx) => {
                if (effectivePin != null) return;
                setCurrentIdx(idx);
              }}
            />
          {/* Weather overlay — reads the currently-displayed frame's
              per-frame weather (backend-computed at the session
              centroid) and renders temp / humidity / wind. Prefers
              the replay pin over the scrub override over the
              auto-loop current index. */}
          {(() => {
            const idx = effectivePin ?? currentIdx;
            const f = windFrames[Math.min(idx, windFrames.length - 1)] ?? null;
            // Wave 20.38c — fire callback with current frame so
            // parent HUDs can read wind + weather in sync with
            // whatever the map is showing.
            if (onWeatherChange) onWeatherChange(f);
            if (!f || hideWeatherChip) return null;
            const timeLabel = f.t
              ? new Date(f.t).toLocaleTimeString(undefined, {
                  hour: 'numeric',
                  minute: '2-digit',
                })
              : '';
            return (
              <Box
                sx={{
                  position: 'absolute',
                  left: 8,
                  top: 8,
                  px: 1.25,
                  py: 1,
                  borderRadius: 1.5,
                  backgroundColor: 'rgba(15,15,20,0.85)',
                  border: `1px solid ${colors.border}`,
                  pointerEvents: 'none',
                  maxWidth: 'calc(100% - 16px)',
                }}
              >
                <Typography sx={{ color: colors.brand, fontWeight: 800, fontSize: '0.65rem', letterSpacing: '0.15em', mb: 0.5 }}>
                  {timeLabel || 'WEATHER'}
                </Typography>
                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                  {f.temperature_c != null && (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <ThermostatIcon sx={{ color: colors.brand, fontSize: 14 }} />
                      <Typography sx={{ color: colors.text, fontWeight: 700, fontSize: '0.75rem' }}>
                        {fmtTemp(f.temperature_c, units)}
                      </Typography>
                    </Stack>
                  )}
                  {f.wind_speed_ms != null && (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <NavigationIcon
                        sx={{
                          color: colors.brand,
                          fontSize: 14,
                          transform: `rotate(${(f.wind_from_deg ?? 0) + 180}deg)`,
                        }}
                      />
                      <Typography sx={{ color: colors.text, fontWeight: 700, fontSize: '0.75rem' }}>
                        {fmtSpeed(f.wind_speed_ms, units)}
                        {f.wind_from_deg != null ? ` ${bearingLabel(f.wind_from_deg)}` : ''}
                      </Typography>
                    </Stack>
                  )}
                  {f.humidity_pct != null && (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <OpacityIcon sx={{ color: colors.brand, fontSize: 14 }} />
                      <Typography sx={{ color: colors.text, fontWeight: 700, fontSize: '0.75rem' }}>
                        {Math.round(f.humidity_pct)}%
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </Box>
            );
          })()}
          {/* Time scrubber. Drag jumps to that frame; release keeps
              the pinned frame until user double-clicks to resume
              auto-loop. */}
          {windFrames.length > 1 && (
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                px: 2,
                py: 1,
                background: 'linear-gradient(to top, rgba(15,15,20,0.9), rgba(15,15,20,0.5) 70%, transparent)',
              }}
              onDoubleClick={() => setScrubOverrideIdx(null)}
            >
              <Slider
                size="small"
                value={scrubOverrideIdx ?? currentIdx}
                min={0}
                max={windFrames.length - 1}
                step={1}
                onChange={(_, v) =>
                  setScrubOverrideIdx(Array.isArray(v) ? v[0] : v)
                }
                sx={{
                  color: colors.brand,
                  '& .MuiSlider-thumb': { width: 12, height: 12 },
                  '& .MuiSlider-track': { border: 'none' },
                  '& .MuiSlider-rail': { opacity: 0.5, backgroundColor: colors.border },
                }}
              />
              <Typography sx={{ color: colors.textFaint, fontSize: '0.6rem', textAlign: 'center', mt: -0.5 }}>
                {scrubOverrideIdx != null ? 'Double-click bar to resume auto-loop' : 'Drag to scrub'}
              </Typography>
            </Box>
          )}
          </>
        );
      })()}
    </Box>
  );
}
